"use client";

/**
 * Next.js Global Error Boundary
 *
 * Catches errors that escape the root layout, including React reconciliation
 * crashes (e.g., from browser translation engines mutating the DOM).
 *
 * This component renders OUTSIDE the root layout, so it must provide its own
 * <html> and <body> tags and cannot use app-level providers or design system
 * components. Styles are inlined to ensure they work without the CSS pipeline.
 */

import { logger } from "@/lib/utils/logger";
import { useEffect } from "react";

export default function GlobalError({
  error,
  reset,
}: Readonly<{
  error: Error & { digest?: string };
  reset: () => void;
}>) {
  useEffect(() => {
    logger.error("Global error boundary caught", error, {
      digest: error.digest,
      url: globalThis.location?.href,
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          fontFamily:
            'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          backgroundColor: "#f8f8f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <div
          style={{
            maxWidth: 440,
            width: "100%",
            padding: "48px 32px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              margin: "0 auto 24px",
              borderRadius: "50%",
              backgroundColor: "#fef2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#dc2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h1
            style={{
              fontSize: 20,
              fontWeight: 600,
              color: "#0d0d0c",
              margin: "0 0 8px",
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: 14,
              color: "#686863",
              margin: "0 0 32px",
              lineHeight: 1.6,
            }}
          >
            An unexpected error occurred. Please reload the page to continue.
          </p>
          <button
            onClick={() => globalThis.location.reload()}
            style={{
              padding: "10px 24px",
              fontSize: 14,
              fontWeight: 500,
              color: "#fff",
              backgroundColor: "#486581",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Reload page
          </button>
        </div>
      </body>
    </html>
  );
}
